import {
  RoomContext,
  useLocalParticipant,
  useMediaDeviceSelect,
  useRoomContext,
  useTrackToggle,
} from '@livekit/components-react';
import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { Room, ConnectionState, DisconnectReason, Track } from 'livekit-client';
import { useFlag } from '@rsos/flags';
import { getLivekitUrl } from '@rsos/utils/metaTags';
import { notifyError } from '@rsos/utils/toastUtils';
import { getDeviceIdForFacingMode } from '../../mediaDeviceUtilities/getDeviceIdForFacingMode';
import { getFacingModeFromDeviceId } from '../../mediaDeviceUtilities/getFacingModeFromDeviceId';
import { LiveVideoService } from '../../shared/LiveVideoService';
import { useRoomMetadata } from '../../shared/useRoomMetadata';
import { useSharedCallerStateForCaller } from '../../shared/useSharedCallerState';
import {
  trackLiveVideoCallerEnteredDarkMode,
  trackLiveVideoCallerJoined,
  trackLiveVideoCallerLeft,
  trackLiveVideoCallerMuted,
  trackLiveVideoCallerTurnedOffCamera,
  trackLiveVideoCallerTurnedOnCamera,
  trackLiveVideoCallerLeftDarkMode,
  trackLiveVideoCallerUnmuted,
  trackLiveVideoTCFlippedCallerCamera,
} from '../trackings';

export interface CallerVideoContextData {
  room: Room;

  joinRoom: (token: string) => Promise<void>;
  leaveRoom: () => Promise<void>;

  isWaitingForSlowAction: boolean;

  isCameraOn: boolean;
  toggleCamera: () => void;

  facingMode: 'environment' | 'user';
  toggleFacingMode: () => void;

  isMuted: boolean;
  setMuted: (mute: boolean) => void;

  isDarkMode: boolean;
  enableDarkMode: () => void;
  disableDarkMode: () => void;

  isLocked: boolean;
}

const serverUrl = getLivekitUrl();

export const CallerVideoContext = createContext<
  CallerVideoContextData | undefined
>(undefined);

export const InternalCallerVideoContextProvider: FunctionComponent<
  PropsWithChildren
> = ({ children }) => {
  // We could reduce the number of rerenders by wrapping each of the callbacks
  // in this context in React.callback; but the video UI is simple and it's not
  // very expensive to rerender, and tracking dependencies is painful and
  // unpleasant.

  const room = useRoomContext();
  const roomMetadata = useRoomMetadata();
  const useNewService = useFlag('use-new-vt911');

  // roomMetadata.isLocked toggles, but the local state isLocked *latches*! Even
  // when the room gets reset and its metadata returns to defaults, keep
  // isLocked true until we actually attempt to reconnecting.
  const [isLocked, setIsLocked] = useState(false);
  useEffect(() => {
    if (roomMetadata.locked) {
      setIsLocked(true);
      room.disconnect().catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error disconnecting from locked room:', error);
      });
    }
  }, [roomMetadata.locked, setIsLocked, room]);

  const [sharedCallerState, setSharedCallerState] =
    useSharedCallerStateForCaller();

  const joinRoom = async (token: string) => {
    setIsLocked(false);
    const API = new LiveVideoService(undefined, false, useNewService);
    if (!token) {
      throw new Error('Missing token to join video room.');
    }

    const liveKitToken = await API.getCallerToken(token);
    if (token && room.state === ConnectionState.Disconnected) {
      await room.connect(serverUrl, liveKitToken);
      trackLiveVideoCallerJoined();
    }
  };

  const { localParticipant } = useLocalParticipant();
  const leaveRoom = async () => {
    if (room.state !== ConnectionState.Disconnected) {
      await room.disconnect();
      // explicitly disable the camera, because sometimes the lil camera light
      // remains lit even after the user has left the call, if they remain on
      // the page.
      await localParticipant.setCameraEnabled(false);
      trackLiveVideoCallerLeft();
    }
  };

  const { toggle: livekitToggleCamera } = useTrackToggle({
    source: Track.Source.Camera,
  });
  const { isCameraEnabled: isCameraOn } = useLocalParticipant();
  const toggleCamera = useCallback(async () => {
    isCameraOn
      ? trackLiveVideoCallerTurnedOffCamera()
      : trackLiveVideoCallerTurnedOnCamera();
    await livekitToggleCamera();
  }, [isCameraOn, livekitToggleCamera]);

  const [isWaitingForSlowAction, setIsWaitingForSlowAction] = useState(false);

  /**
   * Shared State
   *
   * These settings can be set locally by the caller, or remotely by the UNITE
   * dispatcher.
   */

  /**
   * Mute :
   **/
  const setMuted = useCallback(
    (mute: boolean) => {
      mute ? trackLiveVideoCallerMuted() : trackLiveVideoCallerUnmuted();
      return setSharedCallerState({
        isMuted: mute,
      });
    },
    [setSharedCallerState],
  );

  // Keep the microphone state in sync with isMuted, which might be set by the
  // UNITE TC.
  useEffect(() => {
    room.localParticipant
      .setMicrophoneEnabled(!sharedCallerState.isMuted)
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error enabling microphone:', error);
        notifyError('Error enabling microphone.');
      });
  }, [room, sharedCallerState.isMuted]);

  /**
   * Facing Mode:
   *
   * The `isFlipping` flag is used to prevent concurrent calls to
   * `navigator.mediaDevices.enumerateDevices` and
   * `navigator.mediaDevices.getUserMedia`.  It was found through a bug report
   * and testing that sometimes the `setActiveMediaDevice` function returned
   * by the `useMediaDeviceSelect` LiveKit hook will never complete when
   * concurrent calls are made to `getUserMedia` and / or `enumerateDevices`.
   * Whether because of how long it takes to change camera or some bug in
   * safari's WebRTC implementation, the bug only ever materialized on iOS.
   **/
  const isFlipping = useRef(false);
  const { setActiveMediaDevice: setActiveCamera } = useMediaDeviceSelect({
    kind: 'videoinput',
    room,
    requestPermissions: false,
  });

  const getFacingMode = useCallback(async () => {
    const deviceId = room.localParticipant.activeDeviceMap.get('videoinput');
    return await getFacingModeFromDeviceId(deviceId);
  }, [room]);

  const setFacingMode = useCallback(
    async (newFacingMode: 'environment' | 'user') => {
      if (isFlipping.current) {
        // Prevent concurrent calls to getUserMedia and enumerateDevices
        return;
      }
      isFlipping.current = true;
      const currentFacingMode = await getFacingMode();
      if (newFacingMode === currentFacingMode) {
        isFlipping.current = false;
        return;
      }

      setIsWaitingForSlowAction(true);

      try {
        const newDeviceId = await getDeviceIdForFacingMode(newFacingMode);

        if (!newDeviceId) {
          // eslint-disable-next-line no-console
          console.error('No device for facingMode:', newFacingMode);
          isFlipping.current = false;
          return;
        }

        await setActiveCamera(newDeviceId);

        // The actual result mode may not be what we requested; set the shared
        // state from reality:
        const resultMode = await getFacingMode();
        await setSharedCallerState({
          facingMode: resultMode,
        });

        trackLiveVideoTCFlippedCallerCamera(resultMode);
      } finally {
        isFlipping.current = false;
        setIsWaitingForSlowAction(false);
      }
    },
    [setActiveCamera, setSharedCallerState, getFacingMode],
  );

  const toggleFacingMode = useCallback(async () => {
    if (isFlipping.current) {
      // Prevent concurrent calls to getUserMedia and enumerateDevices
      return;
    }
    const currentMode = await getFacingMode();
    const toggledFacingMode = currentMode === 'user' ? 'environment' : 'user';
    await setFacingMode(toggledFacingMode);
  }, [getFacingMode, setFacingMode]);

  // Keep the camera facing state in sync with facingMode, which might be set by
  // the UNITE TC.
  useEffect(() => {
    setFacingMode(sharedCallerState.facingMode).catch(error => {
      // eslint-disable-next-line no-console
      console.error('Error setting camera facing mode:', error);
      notifyError('Error setting camera facing mode.');
    });
  }, [room, setFacingMode, sharedCallerState.facingMode]);

  /**
   * Go dark mode:
   * */
  const enableDarkMode = useCallback(async () => {
    await setSharedCallerState({
      isDarkMode: true,
    });
    trackLiveVideoCallerEnteredDarkMode();
  }, [setSharedCallerState]);

  const disableDarkMode = useCallback(async () => {
    await setSharedCallerState({
      isDarkMode: false,
    });
    trackLiveVideoCallerLeftDarkMode();
  }, [setSharedCallerState]);

  return (
    <CallerVideoContext.Provider
      value={{
        room,
        joinRoom,
        leaveRoom,
        isLocked,
        isCameraOn,
        toggleCamera,
        ...sharedCallerState,
        enableDarkMode,
        disableDarkMode,
        setMuted,
        toggleFacingMode,
        isWaitingForSlowAction,
      }}
    >
      {children}
    </CallerVideoContext.Provider>
  );
};

// In order to have all the livekit hooks work inside the actual context
// provider, we need to set up the room in a parent component.
export const CallerVideoContextProvider: FunctionComponent<
  PropsWithChildren
> = ({ children }) => {
  const [room, setRoom] = useState(new Room());

  // Livekit rooms stop updating after they're disconnected;
  // in order to support reconnecting, create a fresh room
  // each time.
  useEffect(() => {
    function onDisconnected(reason?: DisconnectReason) {
      if (reason === DisconnectReason.CLIENT_INITIATED) {
        setRoom(new Room());
      }
    }
    room.addListener('disconnected', onDisconnected);
    return () => {
      room.removeListener('disconnected', onDisconnected);
    };
  }, [room]);

  return (
    <RoomContext.Provider value={room}>
      <InternalCallerVideoContextProvider>
        {children}
      </InternalCallerVideoContextProvider>
    </RoomContext.Provider>
  );
};
